import { httpsCallable } from 'firebase/functions';

/**
 * `getCapturesStorageUsage` is a callable; some dev setups (localhost + browser CORS / IAM quirks)
 * fail that request even though production works. Skip the pre-check only in those cases — the cap
 * is still enforced server-side on upload/pipeline where applicable.
 */
function shouldSkipCapturesStorageHeadroom() {
  const envSkip = process.env.REACT_APP_SKIP_STORAGE_HEADROOM_CHECK;
  if (envSkip === 'true' || envSkip === '1') {
    return true;
  }
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return false;
  }
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

/**
 * Ensures bytes under users/{uid}/studio/{projectId}/captures/** won't exceed the server cap after an upload.
 * @param {import('firebase/functions').Functions} functions
 * @param {number} additionalBytes
 */
export async function assertCapturesStorageHeadroom(functions, additionalBytes) {
  const add = Number(additionalBytes);
  if (!Number.isFinite(add) || add <= 0) {
    throw new Error('Invalid upload size');
  }
  if (shouldSkipCapturesStorageHeadroom()) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        '[capturesStorageGate] Skipping getCapturesStorageUsage on this origin (localhost dev or REACT_APP_SKIP_STORAGE_HEADROOM_CHECK).'
      );
    }
    return;
  }
  const fn = httpsCallable(functions, 'getCapturesStorageUsage');
  const { data } = await fn();
  if (data?.exempt === true) return;
  const used = Number(data?.usedBytes) || 0;
  const cap = Number(data?.capBytes) || 0;
  if (used + add > cap) {
    const capGb = Math.max(1, Math.round(cap / (1024 * 1024 * 1024)));
    throw new Error(
      `Storage full (${capGb} GB cap for capture files: video, 3D data, frames, thumbnails, etc.). Delete a capture to free space.`
    );
  }
}
